import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { call } from '@/api/desk';
import { api } from '@/lib/api';
import { DataTable, DateInput, Filters, Notice, PageHeader, Sel, Stat, Tabs, arr, classLabel, dt, errMsg, isNA, money, today, useClasses, useGet, useSessions, useTerms } from '@/features/shared/ops/kit';
import { saveBlob } from '@/lib/native';

/* Shapes: API_CHANGES §7 */
const pct = (n?: number | null) => (n == null ? '—' : `${Number(n).toFixed(Number(n) % 1 ? 1 : 0)}%`);
const num = (n?: number | null) => (n == null ? '—' : Number(n).toLocaleString('en-NG'));
const monthStart = () => today().slice(0, 8) + '01';
const daysAgo = (d: number) => new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);

function State({ q, children }: { q: { isLoading: boolean; error: unknown; refetch: () => unknown }; children: React.ReactNode }) {
  if (q.isLoading) return <p className="text-sm text-slate-500 py-6 text-center">Loading report…</p>;
  if (q.error) return <><Notice kind={isNA(q.error) ? 'info' : 'error'}>{isNA(q.error) ? 'This report is not available on the server yet.' : errMsg(q.error)}</Notice>{!isNA(q.error) && <Button variant="ghost" onClick={() => q.refetch()}>Retry</Button>}</>;
  return <>{children}</>;
}

function MiniChart({ data, x, bars }: { data: any[]; x: string; bars: { key: string; name: string; color: string }[] }) {
  if (!data.length) return null;
  return (
    <div className="h-56 mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={x} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={50} />
          <Tooltip />
          {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[3, 3, 0, 0]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function EnrolmentReport() {
  const sessions = useSessions();
  const [sessionId, setSessionId] = useState('');
  const q = useGet<any>('rep-enrolment', '/school/reports/enrolment', { session_id: sessionId });
  const d = q.data;
  return (
    <Card title="Enrolment">
      <Filters><Sel value={sessionId} onChange={setSessionId} placeholder="Current session" options={arr(sessions.data).map((s: any) => ({ value: s.id, label: s.name }))} /></Filters>
      <State q={q}>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Stat label="Active" value={num(d?.totals?.active)} /><Stat label="Boys" value={num(d?.totals?.male)} /><Stat label="Girls" value={num(d?.totals?.female)} />
          <Stat label="Admitted this session" value={num(d?.totals?.admitted_this_session)} /><Stat label="Withdrawn" value={num(d?.totals?.withdrawn)} /><Stat label="Alumni" value={num(d?.totals?.alumni)} />
        </div>
        <div className="mt-4">
          <DataTable rows={arr(d?.by_class)} empty="No classes." cols={[{ head: 'Class', cell: (r: any) => r.class }, { head: 'Boys', right: true, cell: (r: any) => num(r.male) }, { head: 'Girls', right: true, cell: (r: any) => num(r.female) }, { head: 'Total', right: true, cell: (r: any) => num(r.total) }, { head: 'Capacity', right: true, cell: (r: any) => (r.capacity ? `${num(r.capacity)} (${pct((r.total / r.capacity) * 100)})` : '—') }]} />
        </div>
        <MiniChart data={arr(d?.monthly)} x="month" bars={[{ key: 'admitted', name: 'Admitted', color: '#16a34a' }, { key: 'withdrawn', name: 'Withdrawn', color: '#dc2626' }]} />
      </State>
    </Card>
  );
}

export function AttendanceSummaryReport() {
  const classes = useClasses();
  const [classId, setClassId] = useState('');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const q = useGet<any>('rep-attendance', '/school/reports/attendance', { class_id: classId, from, to });
  const d = q.data;
  return (
    <Card title="Attendance">
      <Filters>
        <Sel value={classId} onChange={setClassId} placeholder="All classes" options={arr(classes.data).map((c: any) => ({ value: c.id, label: classLabel(c) }))} />
        <DateInput label="From" value={from} onChange={setFrom} /><DateInput label="To" value={to} onChange={setTo} />
      </Filters>
      <State q={q}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Attendance rate" value={pct(d?.summary?.rate)} /><Stat label="Present" value={num(d?.summary?.present)} /><Stat label="Absent" value={num(d?.summary?.absent)} /><Stat label="Late" value={num(d?.summary?.late)} /><Stat label="Excused" value={num(d?.summary?.excused)} />
        </div>
        <MiniChart data={arr(d?.daily)} x="date" bars={[{ key: 'present', name: 'Present', color: '#16a34a' }, { key: 'absent', name: 'Absent', color: '#dc2626' }]} />
        <div className="mt-4">
          <DataTable rows={arr(d?.by_class)} empty="No attendance marked in this range." cols={[{ head: 'Class', cell: (r: any) => r.class }, { head: 'Present', right: true, cell: (r: any) => num(r.present) }, { head: 'Absent', right: true, cell: (r: any) => num(r.absent) }, { head: 'Late', right: true, cell: (r: any) => num(r.late) }, { head: 'Rate', right: true, cell: (r: any) => pct(r.rate) }]} />
        </div>
      </State>
    </Card>
  );
}

export function FinanceReport() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const q = useGet<any>('rep-finance', '/school/reports/finance', { from, to });
  const d = q.data;
  return (
    <Card title="Finance">
      <Filters><DateInput label="From" value={from} onChange={setFrom} /><DateInput label="To" value={to} onChange={setTo} /></Filters>
      <State q={q}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Invoiced" value={money(d?.invoiced)} /><Stat label="Collected" value={money(d?.collected)} /><Stat label="Outstanding" value={money(d?.outstanding)} /><Stat label="Expenses" value={money(d?.expenses)} /><Stat label="Net" value={money(d?.net)} />
        </div>
        <MiniChart data={arr(d?.daily)} x="date" bars={[{ key: 'amount', name: 'Collected (₦)', color: '#16a34a' }]} />
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <DataTable rows={arr(d?.payments_by_method)} empty="No payments in this period." cols={[{ head: 'Method', cell: (r: any) => String(r.method).replace(/_/g, ' ') }, { head: 'Payments', right: true, cell: (r: any) => num(r.count) }, { head: 'Amount', right: true, cell: (r: any) => money(r.amount) }]} />
          <DataTable rows={arr(d?.expenses_by_category)} empty="No expenses in this period." cols={[{ head: 'Expense category', cell: (r: any) => r.category }, { head: 'Amount', right: true, cell: (r: any) => money(r.amount) }]} />
        </div>
      </State>
    </Card>
  );
}

export function ResultsSummaryReport() {
  const t = useTerms();
  const classes = useClasses();
  const [termId, setTermId] = useState('');
  const [classId, setClassId] = useState('');
  const q = useGet<any>('rep-results', '/school/reports/results', { term_id: termId, class_id: classId });
  const d = q.data;
  return (
    <Card title={`Results${d?.term?.name ? ` · ${d.term.name}` : ''}`}>
      <Filters>
        <Sel value={termId} onChange={setTermId} placeholder="Current term" options={t.terms.map((x: any) => ({ value: x.id, label: x.label }))} />
        <Sel value={classId} onChange={setClassId} placeholder="All classes" options={arr(classes.data).map((c: any) => ({ value: c.id, label: classLabel(c) }))} />
      </Filters>
      <State q={q}>
        <DataTable rows={arr(d?.by_class)} empty="No computed results for this term." cols={[{ head: 'Class', cell: (r: any) => r.class }, { head: 'Students', right: true, cell: (r: any) => num(r.students) }, { head: 'Average', right: true, cell: (r: any) => (r.average != null ? Number(r.average).toFixed(1) : '—') }, { head: 'Highest', right: true, cell: (r: any) => r.highest ?? '—' }, { head: 'Lowest', right: true, cell: (r: any) => r.lowest ?? '—' }, { head: 'Pass rate', right: true, cell: (r: any) => pct(r.pass_rate) }]} />
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <DataTable rows={arr(d?.subjects)} empty="No subjects." cols={[{ head: 'Subject', cell: (r: any) => r.subject }, { head: 'Average', right: true, cell: (r: any) => (r.average != null ? Number(r.average).toFixed(1) : '—') }, { head: 'Highest', right: true, cell: (r: any) => r.highest ?? '—' }, { head: 'Lowest', right: true, cell: (r: any) => r.lowest ?? '—' }]} />
          <DataTable rows={arr(d?.grades)} empty="No grades." cols={[{ head: 'Grade', cell: (r: any) => r.grade }, { head: 'Students', right: true, cell: (r: any) => num(r.count) }]} />
        </div>
      </State>
    </Card>
  );
}

/* ---------- CSV export: POST reports/export -> poll exports/{id} -> download ---------- */
const REPORTS = [
  { value: 'enrolment', label: 'Enrolment' }, { value: 'attendance', label: 'Attendance' }, { value: 'finance', label: 'Finance', finance: true },
  { value: 'results', label: 'Results' }, { value: 'students', label: 'Students' }, { value: 'payments', label: 'Payments', finance: true }, { value: 'debtors', label: 'Debtors', finance: true },
];

export function ExportPanel({ allowFinance = true, only }: { allowFinance?: boolean; only?: string[] }) {
  const t = useTerms();
  const classes = useClasses();
  const options = REPORTS.filter((r) => (allowFinance || !r.finance) && (!only || only.includes(r.value)));
  const [report, setReport] = useState(options[0]?.value ?? 'enrolment');
  const [p, setP] = useState({ from: monthStart(), to: today(), term_id: '', class_id: '' });
  const [exportId, setExportId] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const status = useQuery({
    queryKey: ['report-export', exportId],
    queryFn: async () => (await call<any>('get', `/school/exports/${exportId}`)).data,
    enabled: exportId != null,
    refetchInterval: (q) => (q.state.data?.status === 'queued' ? 2000 : false),
    retry: false,
  });
  useEffect(() => setErr(''), [report]);
  const ex = status.data;

  async function start() {
    setErr('');
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(p).filter(([, v]) => v !== ''));
      const r = await call<any>('post', '/school/reports/export', { body: { report, format: 'csv', params } });
      setExportId(r.data.id);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  async function download() {
    if (!ex) return;
    setErr('');
    try {
      const url = String(ex.download_url || `/school/exports/${ex.id}/download`).replace(/^.*\/api\/v1/, '');
      const res = await api.get(url, { responseType: 'blob' });
      await saveBlob(res.data as Blob, `${ex.type ?? report}-${today()}.csv`);
    } catch (e) {
      setErr(errMsg(e));
    }
  }
  const needsDates = ['attendance', 'finance', 'payments'].includes(report);
  const needsTerm = ['results'].includes(report);
  return (
    <Card title="Export to CSV">
      {err && <Notice kind="error">{err}</Notice>}
      <Filters>
        <Sel value={report} onChange={(v) => setReport(v || options[0]?.value || 'enrolment')} placeholder="Choose report" options={options} />
        {needsDates && <><DateInput label="From" value={p.from} onChange={(v) => setP({ ...p, from: v })} /><DateInput label="To" value={p.to} onChange={(v) => setP({ ...p, to: v })} /></>}
        {needsTerm && <Sel value={p.term_id} onChange={(v) => setP({ ...p, term_id: v })} placeholder="Current term" options={t.terms.map((x: any) => ({ value: x.id, label: x.label }))} />}
        {['attendance', 'results', 'students', 'debtors'].includes(report) && <Sel value={p.class_id} onChange={(v) => setP({ ...p, class_id: v })} placeholder="All classes" options={arr(classes.data).map((c: any) => ({ value: c.id, label: classLabel(c) }))} />}
        <Button disabled={busy} onClick={start}>{busy ? 'Starting…' : 'Export'}</Button>
      </Filters>
      {exportId != null && (
        <div className="text-sm flex items-center gap-3 flex-wrap">
          {status.isLoading ? 'Preparing…' : status.error ? <span className="text-red-600">{errMsg(status.error)}</span> : ex && (
            <>
              <StatusBadge status={ex.status} />
              {ex.rows != null && <span>{num(ex.rows)} rows</span>}
              {ex.expires_at && <span className="text-slate-500">Available until {dt(ex.expires_at)}</span>}
              {ex.status === 'ready' && <Button onClick={download}>Download CSV</Button>}
              {ex.status === 'failed' && <span className="text-red-600">The export failed. Try again.</span>}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/** Full reports page (owner). Desk passes allowFinance={false}: the finance report is forbidden for desk. */
export function SchoolReportsPage({ allowFinance = true }: { allowFinance?: boolean }) {
  const tabs = [{ id: 'enrolment', label: 'Enrolment' }, { id: 'attendance', label: 'Attendance' }, ...(allowFinance ? [{ id: 'finance', label: 'Finance' }] : []), { id: 'results', label: 'Results' }, { id: 'export', label: 'Export' }];
  const [tab, setTab] = useState('enrolment');
  return (
    <div>
      <PageHeader title="Reports" sub="Enrolment, attendance, finance and results summaries, with CSV export" />
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === 'enrolment' && <EnrolmentReport />}
      {tab === 'attendance' && <AttendanceSummaryReport />}
      {tab === 'finance' && allowFinance && <FinanceReport />}
      {tab === 'results' && <ResultsSummaryReport />}
      {tab === 'export' && <ExportPanel allowFinance={allowFinance} />}
    </div>
  );
}
